import React from "react";

import Circle from "./CircleProgress";
import { Button, Typography } from "@mui/material";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import Divider from '@mui/material/Divider';
import * as util from "../utils/util";
import BeenhereIcon from "@mui/icons-material/Beenhere";
import VerifiedIcon from '@mui/icons-material/Verified';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';

/**
 * @param healthInfo 스토어에 담긴 항목 값 (title·target·kcal·unitType·infoText·navi)
 * @param title      항목 이름. 조회 전이거나 응답에 이 항목이 없어도 카드 이름은 보여준다.
 * @param loading    조회 중이면 값 자리에 스켈레톤을 그린다.
 *
 * 예전에는 healthInfo 가 비어 있어도 그대로 그려서, 제목 없는 카드에 0 이 찍히고
 * 목표 달성 메달까지 붙었다(0 <= 0). 사용자에게는 카드가 안 보이거나 잘못된 값으로 보였다.
 */
/** unitType → 문구에 쓸 단위 표기 */
const UNIT_LABEL: { [key: string]: string } = { kcal: 'kcal', cup: '잔', step: '걸음' };

/**
 * 카드 하단 문구.
 *
 * 원래는 서버가 주는 `health_data[항목]` 을 그대로 썼다. 그런데 실측(2026-08-18,
 * 계정 20260010·20260013 동일) AP_002_001 응답의 result_data 에는 `health`·`target`
 * 뿐이고 **`health_data` 키가 아예 없다.** 그래서 이 줄이 계속 비어 있었다.
 *
 * 서버가 다시 주기 시작하면 그 값을 그대로 쓰고(호출부에서 우선 적용), 없는 동안에는
 * 목표 대비 상태를 만들어 채운다. 혈당 카드(HistoryBloodInputCard)와 기기 카드가
 * 이미 같은 방식으로 하단 문구를 직접 만든다.
 */
const progressText = (info: any): string => {
  const target = Number(info.target) || 0;
  const value = Number(info.kcal) || 0;
  const unit = UNIT_LABEL[info.unitType] ?? util.strNull(info.unitType);

  if (value <= 0) {
    return '오늘 기록이 없습니다.';
  }
  if (target <= 0) {
    return `오늘 ${util.formatComma(value)}${unit} 기록했습니다.`;
  }
  if (value >= target) {
    return '오늘 목표를 달성했습니다!';
  }
  return `목표까지 ${util.formatComma(Math.round(target - value))}${unit} 남았습니다.`;
};

const HistoryInputCard = (props: any) => {
  const info = props.healthInfo ?? {};
  const loading = props.loading === true;
  // 이 항목의 응답이 실제로 들어왔는지 — 제목은 응답을 받은 뒤에만 채워진다
  const hasData = util.strNull(info.title) !== '';
  const title = hasData ? info.title : util.strNull(props.title);

  const target = Number(info.target);
  const value = Number(info.kcal);
  const ready = hasData && Number.isFinite(target) && target > 0 && Number.isFinite(value);
  // 목표가 0 이면 0/0 = NaN 이 되어 MUI 에 NaN 이 그대로 들어간다
  const progress = ready ? Math.min(100, (value / target) * 100) : 0;
  // 미측정(0/0)에 달성 메달이 붙지 않도록 목표가 있는 경우만 판정한다
  const achieved = ready && value >= target;

  return (
    <Box alignItems="flex-start" sx={Card_Style} >

      <Typography sx={{ textAlign: 'center', fontSize: 14, fontWeight: 700, p: '15px 0 15px 0' }}> {title} </Typography>
      <Divider />
      <Box sx={{ p: 0.5 }}>
        <Stack justifyContent={'center'} alignItems={'center'}>
          <Box>
            {loading ? (
              <Skeleton animation="wave" variant="circular" width={100} height={100} />
            ) : (
              <Circle progress={{ goal: info.target, value: info.kcal }} />
            )}
          </Box>
          <Box sx={{ pb: 1 }}>
            <Button onClick={info.navi} disabled={!hasData} sx={{ flexWrap: 'wrap', p: 0 }}>

              {achieved ? (
                <MilitaryTechIcon sx={{ fontSize: 30,  color: '#ffc007',}} />
              ) : (
                null
              )}
              <Stack direction={'row'} alignItems={'baseline'}>
                {loading ? (
                  <Skeleton animation="wave" variant="rounded" width={70} height={26} sx={{ borderRadius: 3 }} />
                ) : (
                  <>
                    <Typography sx={{ color: '#555', fontSize: 22, pr: '5px', fontWeight: 700 }}>
                      {hasData ? util.formatComma(info.kcal) : '-'}
                    </Typography>
                    <Typography sx={{ color: '#888', fontSize: 14, textTransform: 'none' }}>{info.unitType} </Typography>
                  </>
                )}
              </Stack>
            </Button>
          </Box>
          <Box sx={{ pb: 2 }}>
            <Button onClick={info.navi} disabled={!hasData} sx={{ p: 0, border: '1px solid #EF8322', borderRadius: 5 }}>
              <EditNoteRoundedIcon sx={{ ml: 1, color: '#EF8322' }} />
              <Typography sx={{ color: '#EF8322', fontSize: 14, pr: 1, fontWeight: 700, pl: .5 }}>입력하기</Typography>
            </Button>
          </Box>
        </Stack>
        <Divider />
        <Box sx={{ p: 0.5, }}>
          <Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 32, color: hasData ? '#555' : '#999' }}>
            {/* 서버 문구(health_data)가 오면 그대로, 없으면 목표 대비 상태를 만들어 쓴다 */}
            {loading
              ? ''
              : !hasData
                ? '기록을 불러오지 못했습니다.'
                : util.strNull(info.infoText) !== ''
                  ? info.infoText
                  : progressText(info)}
          </Typography>
        </Box>
      </Box>

      <BorderLinearProgress variant="determinate" value={progress}
        sx={{ borderRadius: '0 0 20px 20px', bgcolor: '#eaeaea' }}
      />
    </Box>
  );
  {/* <ListItem alignItems="flex-start" sx={Card_Style} >
          <ListItemAvatar>
              <Avatar alt="Remy Sharp" src="/static/images/avatar/1.jpg" />
          </ListItemAvatar>
          <ListItemText
          primary ={title} 
          secondary={
              <Box>
                  <Box>{props.values.kcal} {props.values.unitType} 
                      <Button onClick={props.values.navi}>
                          <ModeIcon />
                      </Button>
                  </Box>
                  {props.values.emptyText}
                  <LinearProgress variant="determinate" value={progress} />
              </Box>
          }
          >
          </ListItemText>
      </ListItem> 
  );*/}
}

const BorderLinearProgress = styled(LinearProgress)({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#eaeaea',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#EF8322',
  },
});

const Card_Style = {
  borderRadius: '10px',
  background: '#fff',
  boxShadow: '0 0 6px  rgba(0,0,0,0.2)',

  flex: '0 50%',
  maxWidth: '100%'
};

export default HistoryInputCard;